import React, { Fragment, useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators / Selectors

import { changeData } from '@actions/ssap/ssapActionCreators';
import { selectIfSogiFeatureEnabled } from '@reducers/appReducer';

// Utils

import { cloneObject } from '../../../utils/miscellaneous/cloneObject';
import { normalizeNameOfPerson } from '../../../utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '../../../utils/tools/preventerTools';
import { questionsVisibility } from '../../../utils/miscellaneous/pageAndQuestionsVisibility';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { ETHNICITY_LIST, OTHER_ETHNICITY_CODE } from '@constants/ssap/hhmTypes/ethnicityList';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { RACES_LIST, OTHER_RACE_CODE, AI_AN_CODE, MIDDLE_EASTERN_RACE_CODE } from '@constants/ssap/hhmTypes/racesList';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - Page 'Ethnicities and Races'
 *
 */
const Page2_10 = props => {

    const {
        data,
        environment: {
            exchangeName,
            isState,
            language,
            localDates: {
                serverDate
            }
        },
        form,
        formName,
        householdMembers,
        messages
    } = props;

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const {
        hhmIndex : member,
        currentHhm
    } = props;

    const {
        ethnicityAndRace : {
            hispanicLatinoSpanishOriginIndicator
        },
        genderIdentification,
        name,
        sexualOrientation
    } = currentHhm ? currentHhm : { ethnicityAndRace: {}, name: {} };

    const isSogiFeatureEnabled = useSelector(selectIfSogiFeatureEnabled);

    const isSogiShown = questionsVisibility.isSogiShown({
        hhm: currentHhm,
        isSogiFeatureEnabled,
        serverDate
    });

    const [ localState, setLocalState ] = useState({
        ethnicities: currentHhm ? prepareValuesForState('ethnicities'): [],
        races:  currentHhm ? prepareValuesForState('races') : []
    });

    const {
        ethnicities,
        races
    } = localState;

    const cleanProp = values => {
        return values.reduce((entities, entity) => {

            const {
                code,
                label
            } = entity;

            if(entity[code]) {
                return [
                    ...entities,
                    {
                        code,
                        label: messages[language][label] ? messages[language][label] : label
                    }
                ];
            } else {
                return entities;
            }
        }, []);
    };

    // Each of the following useEffects is invoked in case if related to its property in Local State has been updated
    // As a result we update JSON in Redux Store

    useEffect(() => {
        const newState = { ...data };

        newState
            .taxHousehold
            .householdMember[member]
            .ethnicityAndRace
            .ethnicity = cleanProp(localState.ethnicities);

        props.actions.changeData(newState);

    }, [localState.ethnicities]);

    useEffect(() => {
        const newState = { ...data };

        newState
            .taxHousehold
            .householdMember[member]
            .ethnicityAndRace
            .race = cleanProp(localState.races);

        newState
            .taxHousehold
            .householdMember[member]
            .americanIndianAlaskaNative
            .americanIndianAlaskaNativeSelfIdentification = localState
                .races
                .map(race => ({ ...race }))
                .filter(race => race[race.code])
                .some(race =>  race.code === AI_AN_CODE );

        const isSomeoneAIANSelfIdentified = newState
            .taxHousehold
            .householdMember
            .some(hhm => hhm.americanIndianAlaskaNative.americanIndianAlaskaNativeSelfIdentification);

        if(isSomeoneAIANSelfIdentified) {
            newState
                .taxHousehold
                .thCommonParams
                .hasSomeoneAIANSelfIdentified = true;
        }

        props.actions.changeData(newState);

    }, [localState.races]);

    // Here we get initialState for Local State

    function prepareValuesForState(type) {

        switch(type) {

            case 'ethnicities':

                return ETHNICITY_LIST.map(ethnicity => {

                    const matchingEthnicity = currentHhm
                        .ethnicityAndRace
                        .ethnicity
                        .find(eth => eth.code === ethnicity.code);

                    return {
                        ...ethnicity,
                        label: ethnicity.code === OTHER_ETHNICITY_CODE ?
                            matchingEthnicity && matchingEthnicity.label : ethnicity.label,
                        [ethnicity.code]: !!matchingEthnicity
                    };
                });

            case 'races':

                return RACES_LIST
                    .reduce(
                        (filteredList, race) => {
                            const { code } = race;

                            if (code === MIDDLE_EASTERN_RACE_CODE && !(isState.NV || isState.IL)) {
                                return filteredList;
                            } else {
                                return [
                                    ...filteredList,
                                    race
                                ];
                            }
                        },
                        [ ]
                    )
                    .map(race => {

                        const matchingRace = currentHhm
                            .ethnicityAndRace
                            .race
                            .find(r => r.code === race.code);

                        return {
                            ...race,
                            label: race.code === OTHER_RACE_CODE ?
                                matchingRace && matchingRace.label : race.label,
                            [race.code]: !!matchingRace
                        };
                    });
        }
    }

    // Here we update JSON in Redux store

    const onFieldChange = newData => {
        const newState = { ...data };
        // Here we check <hispanicLatinoSpanishOriginIndicator> and if its value is false
        // clean up <ethnicity> Array

        if(!newData.ethnicityAndRace.hispanicLatinoSpanishOriginIndicator) {
            newData.ethnicityAndRace.ethnicity = [];
        }
        newState.taxHousehold.householdMember[member] = newData;
        props.actions.changeData(newState);

        // Updating of Local State: uncheck all ethnicities

        setLocalState(state => ({
            ...state,
            ethnicities: state.ethnicities.map(ethnicity => ({
                ...ethnicity,
                [ethnicity.code]: false
            }))
        }));
    };

    // Updating of Local State: check or uncheck certain ethnicity

    const onFieldChange_ethnicity = newData => {

        setLocalState(state => ({
            ...state,
            ethnicities: state.ethnicities.map(ethnicity => ethnicity.code === newData.code ?
                {
                    ...newData,
                    label: ethnicity.code === OTHER_ETHNICITY_CODE ? newData.ethnicityLabel : newData.label
                } : ethnicity
            ) }));
    };

    // Updating of Local State: check or uncheck certain race

    const onFieldChange_race = newData => {
        setLocalState(state => ({
            ...state,
            races: state.races.map(race => race.code === newData.code ?
                {
                    ...newData,
                    label: race.code === OTHER_RACE_CODE ? newData.raceLabel : newData.label
                } : race
            )
        }));
        if (newData.code === AI_AN_CODE) {

            const newState = { ...data };

            newState
                .taxHousehold
                .householdMember[member]
                .americanIndianAlaskaNative = cloneObject(EMPTY_OBJECTS.TRIBE_STATUS);

            newState
                .taxHousehold
                .householdMember[member]
                .incomes = newState
                    .taxHousehold
                    .householdMember[member]
                    .incomes
                    .map(i => ({
                        ...i,
                        tribalAmount: 0
                    }));

            props.actions.changeData(newState);
        }
    };

    const topLevelValidity = races.filter(r => r[r.code]).length <= 10;

    return (
        <div
            className = 'fade-in'
            id = 'fhEthinicity'
        >
            <div className = 'usa-alert usa-alert--info gi-alert gi-alert__info' >
                <div className = 'usa-alert__body gi-alert__info__body' >
                    <p className = 'usa-alert__text gi-alert__info__text' >
                        <FormattedMessage
                            id = 'ssap.family_household.ethnicity.notification'
                            values = { {
                                exchangeName,
                                lineBreak: <br/>
                            } }
                        />
                    </p>
                </div>
            </div>
            <Form
                { ...{
                    formName,
                    topLevelValidity
                } }
            >
                <div className = 'subsection'>
                    <UniversalInput
                        legend = { {
                            legendText: 'ssap.input.legend.is_hispanic',
                            legendTextReplacements: {
                                personName: (
                                    <strong>
                                        { normalizeNameOfPerson(name) }
                                    </strong>
                                )
                            }
                        } }
                        label = { {
                            showRequiredIcon: false
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.RADIO_BUTTONS,
                            name: 'hispanicLatinoSpanishOriginIndicator',
                            value: hispanicLatinoSpanishOriginIndicator,
                            options: DROPDOWN_OPTIONS.YES_NO_PREFER_NOT_TO_ANSWER,
                            currentData: householdMembers[member],
                            fields: [ 'ethnicityAndRace' ],
                            isInputStandalone: true
                        } }
                        inputActions = { {
                            onFieldChange
                        } }
                    />

                    {

                        hispanicLatinoSpanishOriginIndicator &&

                        <Fragment>
                            <div className = 'margin-t-30' >
                                <fieldset className = 'usa-fieldset' >
                                    <legend className = 'usa-legend gi-checkbox margin-b-10' >
                                        <FormattedMessage
                                            id = 'ssap.family_household.ethnicity.ethnicity'
                                            tagName = 'strong'
                                        />&nbsp;
                                        <FormattedMessage id = 'ssap.family_household.ethnicity.check_all_ethnicities' />
                                    </legend>
                                    <ul className = 'usa-list usa-list--unstyled' >
                                        {
                                            ethnicities.map(ethnicity => {
                                                return (
                                                    <Fragment key = { ethnicity.code } >
                                                        <li>
                                                            <UniversalInput
                                                                label = { {
                                                                    text: ethnicity.code !== OTHER_ETHNICITY_CODE
                                                                        ? ethnicity.label
                                                                        : 'ssap.input.label.other'
                                                                } }
                                                                inputData = { {
                                                                    isInputRequired: false,
                                                                    classes: [ 'layout--01' ],
                                                                    type: INPUT_TYPES.CHECKBOX,
                                                                    name: ethnicity.code,
                                                                    checked: ethnicity[ethnicity.code],
                                                                    value: ethnicity[ethnicity.code],
                                                                    currentData: ethnicity,
                                                                    fields: []
                                                                } }
                                                                inputActions = { {
                                                                    onFieldChange: onFieldChange_ethnicity
                                                                } }
                                                            />
                                                        </li>
                                                        {
                                                            ethnicity.code === OTHER_ETHNICITY_CODE && ethnicity[ethnicity.code] &&

                                                            <li>
                                                                <UniversalInput
                                                                    label = { {
                                                                        text: 'ssap.input.label.please_specify',
                                                                        labelClasses: [ 'spl__width--01' ]
                                                                    } }
                                                                    inputData = { {
                                                                        isInputRequired: false,
                                                                        type: INPUT_TYPES.REGULAR_INPUT,
                                                                        name: 'ethnicityLabel',
                                                                        helperClasses: [ 'margin-t-0' ],
                                                                        value: ethnicity.label,
                                                                        placeholder: 'ssap.input.placeholder.other_ethnicity',
                                                                        validationType: VALIDATION_TYPES.OTHER_RACE_OR_ETHNICITY,
                                                                        currentData: ethnicity,
                                                                        fields: []
                                                                    } }
                                                                    inputActions = { {
                                                                        onFieldChange: onFieldChange_ethnicity
                                                                    } }
                                                                />
                                                            </li>
                                                        }
                                                    </Fragment>
                                                );
                                            })
                                        }
                                    </ul>
                                </fieldset>
                            </div>
                        </Fragment>
                    }

                    <Fragment>
                        <div className = 'margin-t-30' >
                            <fieldset className = 'usa-fieldset' >
                                <legend className = 'usa-legend gi-checkbox margin-b-10' >
                                    <FormattedMessage
                                        id = 'ssap.family_household.ethnicity.race'
                                        tagName = 'strong'
                                    />&nbsp;
                                    <FormattedMessage id = 'ssap.family_household.ethnicity.check_all_races' />
                                </legend>
                                <ValidationErrorMessage
                                    isVisible = { isFormSubmitted && !topLevelValidity }
                                    messageText = { VALIDATION_ERROR_MESSAGES.RACES_QTY_EXCEEDING }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                                <ul className = 'usa-list usa-list--unstyled' >
                                    {
                                        races.map(race => {
                                            return (
                                                <Fragment key = { race.code } >
                                                    <li>
                                                        <UniversalInput
                                                            label = { {
                                                                text:  race.code !== OTHER_RACE_CODE
                                                                    ? race.label
                                                                    : 'ssap.input.label.other'
                                                            } }
                                                            inputData = { {
                                                                isInputRequired: false,
                                                                type: INPUT_TYPES.CHECKBOX,
                                                                classes: [ 'layout--01' ],
                                                                name: race.code,
                                                                checked: race[race.code],
                                                                value: race[race.code],
                                                                currentData: race,
                                                                fields: []
                                                            } }
                                                            inputActions = { {
                                                                onFieldChange: onFieldChange_race
                                                            } }
                                                        />
                                                    </li>
                                                    {
                                                        race.code === OTHER_RACE_CODE && race[race.code] &&

                                                        <li>
                                                            <UniversalInput
                                                                label = { {
                                                                    text: 'ssap.input.label.please_specify',
                                                                    labelClasses: [ 'spl__width--01' ]
                                                                } }
                                                                inputData = { {
                                                                    isInputRequired: false,
                                                                    type: INPUT_TYPES.REGULAR_INPUT,
                                                                    name: 'raceLabel',
                                                                    helperClasses: [ 'margin-t-0' ],
                                                                    value: race.label,
                                                                    placeholder: 'ssap.input.placeholder.other_race',
                                                                    validationType: VALIDATION_TYPES.OTHER_RACE_OR_ETHNICITY,
                                                                    currentData: race,
                                                                    fields: []
                                                                } }
                                                                inputActions = { {
                                                                    onFieldChange: onFieldChange_race
                                                                } }
                                                            />
                                                        </li>
                                                    }
                                                </Fragment>
                                            );
                                        })
                                    }
                                </ul>
                            </fieldset>
                        </div>
                    </Fragment>
                </div>

                {
                    isSogiShown
                        ? (
                            <div className = 'subsection' data-testid = 'sogi-subsection' >
                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.gender_identification',
                                        legendTextReplacements: {
                                            personName: (
                                                <strong>
                                                    { normalizeNameOfPerson(name) }
                                                </strong>
                                            )
                                        }
                                    } }
                                    label = { {
                                        showRequiredIcon: false
                                    } }
                                    inputData = { {
                                        isInputRequired: false,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'genderIdentification',
                                        value: genderIdentification,
                                        options: DROPDOWN_OPTIONS.GENDER_IDENTIFICATION,
                                        currentData: householdMembers[member],
                                        fields: [ ],
                                        isInputStandalone: true
                                    } }
                                    inputActions = { {
                                        onFieldChange
                                    } }
                                />

                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.sexual_orientation',
                                        legendTextReplacements: {
                                            personName: (
                                                <strong>
                                                    { normalizeNameOfPerson(name) }
                                                </strong>
                                            )
                                        }
                                    } }
                                    label = { {
                                        showRequiredIcon: false
                                    } }
                                    inputData = { {
                                        isInputRequired: false,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'sexualOrientation',
                                        value: sexualOrientation,
                                        options: DROPDOWN_OPTIONS.SEXUAL_ORIENTATION,
                                        currentData: householdMembers[member],
                                        fields: [ ],
                                        isInputStandalone: true
                                    } }
                                    inputActions = { {
                                        onFieldChange
                                    } }
                                />
                            </div>
                        )
                        : null
                }

            </Form>
        </div>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    messages: state.common.messages,

    data: state.ssap.data,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_10);
